// --- External Imports ---
import { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown as ChevronDownIcon, X as XIcon } from 'lucide-react';
import ZINDEXES from '@/constants/zIndexes';
import { generateId, isFunction } from '@/utils';
import Button from '@/components/Button/Button';

const Select = ({
    label,
    placeholder = 'Please select...',
    options,
    keepOpenAfterSelect = false, // ***not working - need to work on***
    onChange,
    inputValue,
    maxDropdownHeight = 200,
    compact,
    getLabel,
    labelKey,
    valueKey = 'value',
    returnFullObject = false,
    disabled = false,
    multi = false,
    loading,
    searchProps,
    uid,
}) => {
    const id = 'drop-down-container-' + (uid || generateId());
    const buttonRef = useRef(null);
    const optionsRef = useRef(null);
    const searchInputRef = useRef(null);
    const [searchString, setSearchString] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [devError, setDevError] = useState('');

    const filteredOptions = useMemo(() => {
        if (searchProps?.enabled && searchString) {
            if (
                !Array.isArray(searchProps.keyArray) ||
                searchProps.keyArray.length === 0
            ) {
                setDevError('searchProps.keyArray is required');
                return [];
            }

            const validKeys = searchProps.keyArray.every(
                (key) => key in options[0]
            );

            if (!validKeys) {
                setDevError('Invalid key in searchProps.keyArray');
                return [];
            }

            const query = searchString.toLowerCase();

            return options.filter((option) =>
                searchProps.keyArray.some((key) =>
                    option[key]?.toString().toLowerCase().includes(query)
                )
            );
        }

        return options;
    }, [searchString, options]);

    const positionAbove = useMemo(() => {
        if (!buttonRef.current) return false;
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;

        return spaceBelow < maxDropdownHeight;
    }, [windowHeight, isOpen]);

    // normalize selected values (single or multiple)
    const selectedValues = useMemo(() => {
        if (multi) {
            if (!Array.isArray(inputValue)) return [];
            return inputValue;
        } else {
            return returnFullObject
                ? inputValue
                : options.find((item) => item[valueKey] === inputValue);
        }
    }, [inputValue, options, multi, returnFullObject, valueKey]);

    const getItemLabel = isFunction(getLabel)
        ? getLabel
        : (item) => {
              return item?.[labelKey] || null;
          };

    const handleDropDownStyle = () => {
        const rect = buttonRef.current.getBoundingClientRect();

        const style = {
            position: 'absolute',
            left: rect.left + window.scrollX + 'px',
            width: rect.width + 'px',
            zIndex: ZINDEXES.DROPDOWN,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: '4px',
            maxHeight: `${maxDropdownHeight}px`,
            overflowY: 'auto',
        };

        if (positionAbove) {
            style.bottom =
                window.innerHeight - rect.top + window.scrollY + 1 + 'px';
        } else {
            style.top = window.scrollY + rect.bottom + 'px';
        }

        setDropdownStyle(style);
    };

    const toggleOpen = () => {
        if (disabled) return;

        if (!isOpen && buttonRef.current) {
            handleDropDownStyle();
        }

        setIsOpen((prev) => !prev);
    };

    const closeOptions = () => {
        setIsOpen(false);
    };

    function setBtnStyles() {
        let result = `w-full py-1 bg-white text-gray-700
        border border-gray-300 rounded-md shadow-sm
        focus:ring-2 focus:ring-blue-500 focus:border-blue-500
        flex items-center justify-between`;

        if (compact) {
            result += ' px-1 text-sm';
        } else {
            result += ' px-4 text-md';
        }

        if (disabled || loading) {
            result += ' cursor-not-allowed opacity-50';
        }

        return result;
    }

    const toggleMultiValue = (option) => {
        const value = returnFullObject ? option : option[valueKey];

        let newSelected;

        if (
            selectedValues.some((v) =>
                returnFullObject
                    ? v[valueKey] === option[valueKey]
                    : v === value
            )
        ) {
            // remove
            newSelected = selectedValues.filter((v) =>
                returnFullObject
                    ? v[valueKey] !== option[valueKey]
                    : v !== value
            );
        } else {
            // add
            newSelected = [...selectedValues, value];
        }

        onChange(newSelected);

        if (!keepOpenAfterSelect) {
            closeOptions();
        }
    };

    const handleNewValue = (newVal) => {
        onChange(newVal);

        if (!keepOpenAfterSelect) {
            closeOptions();
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => handleDropDownStyle(), [windowHeight]);

    useEffect(() => {
        function handleDocumentClick(e) {
            if (
                !buttonRef.current?.contains(e.target) &&
                !optionsRef.current?.contains(e.target)
            ) {
                closeOptions();
            }
        }

        function handleScrollView(e) {
            if (
                !buttonRef.current?.contains(e.target) &&
                !optionsRef.current?.contains(e.target)
            ) {
                closeOptions();
            }
        }

        document.addEventListener('click', handleDocumentClick);
        window.addEventListener('scroll', handleScrollView, true);

        return () => {
            document.removeEventListener('click', handleDocumentClick);
            window.removeEventListener('scroll', handleScrollView, true);
        };
    }, []);

    useEffect(() => {
        if (isOpen && searchProps?.enabled) {
            // timeout ensures DOM is ready after portal render
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 0);
        }
    }, [isOpen, searchProps?.enabled]);

    return (
        <div className="w-full">
            {label && <span className="text-xs">{label}</span>}

            <button
                ref={buttonRef}
                type="button"
                className={setBtnStyles()}
                onClick={toggleOpen}
                disabled={loading}
            >
                {multi ? (
                    <div className="flex overflow-hidden gap-1">
                        {selectedValues.length > 0
                            ? selectedValues.map((val, index) =>
                                  returnFullObject ? (
                                      <span
                                          key={val[valueKey]}
                                          className="text-nowrap text-sm"
                                      >
                                          {val[valueKey]}
                                          {index < selectedValues.length - 1 &&
                                              ', '}
                                      </span>
                                  ) : (
                                      <span
                                          key={val}
                                          className="text-nowrap text-sm"
                                      >
                                          {
                                              options.find(
                                                  (option) =>
                                                      option[valueKey] === val
                                              )?.[valueKey]
                                          }
                                          {index < selectedValues.length - 1 &&
                                              ', '}
                                      </span>
                                  )
                              )
                            : placeholder}
                    </div>
                ) : (
                    <span className="w-[95%] text-start overflow-hidden text-ellipsis whitespace-nowrap">
                        {getItemLabel(selectedValues) || placeholder}
                    </span>
                )}

                <ChevronDownIcon
                    className="inline ml-2 h-4 w-4"
                    style={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                    }}
                />
            </button>

            {isOpen &&
                createPortal(
                    <ul
                        id={id}
                        style={dropdownStyle}
                        className="py-2 mt-[1px] border border-gray-300 rounded-md shadow-lg flex flex-col"
                        ref={optionsRef}
                    >
                        {searchProps?.enabled && !positionAbove && (
                            <li
                                key={() => generateId()}
                                className="flex justify-between mx-2 mb-1 p-1 border border-secandary-600 rounded hover:border-secandary-600 focus:border-secandary-600"
                            >
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    autoComplete="off"
                                    name="searchInput"
                                    className="outline-none"
                                    value={searchString}
                                    onChange={(e) =>
                                        setSearchString(e.target.value)
                                    }
                                />

                                {Boolean(searchString) && (
                                    <Button
                                        icon={XIcon}
                                        iconSize={12}
                                        appearance="flat"
                                        variant="neutral"
                                        size="xs"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchString('');
                                        }}
                                    />
                                )}
                            </li>
                        )}

                        {devError ? (
                            <span className="px-2 text-warning text-sm">
                                {devError}
                            </span>
                        ) : (
                            filteredOptions.map((option) => {
                                const isSelected = multi
                                    ? selectedValues.some((v) =>
                                          returnFullObject
                                              ? v[valueKey] === option[valueKey]
                                              : v === option[valueKey]
                                      )
                                    : selectedValues?.[valueKey] ===
                                      option[valueKey];

                                return (
                                    <li
                                        key={option[valueKey]}
                                        className={`cursor-pointer py-1 px-2 flex items-center gap-2 ${
                                            isSelected &&
                                            'bg-primary-50 text-primary-900'
                                        }`}
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            multi
                                                ? toggleMultiValue(option)
                                                : handleNewValue(
                                                      returnFullObject
                                                          ? option
                                                          : option[valueKey]
                                                  );
                                        }}
                                    >
                                        {multi && (
                                            <input
                                                type="checkbox"
                                                readOnly
                                                checked={isSelected}
                                                className="cursor-pointer"
                                            />
                                        )}
                                        <>{getItemLabel(option)}</>
                                    </li>
                                );
                            })
                        )}

                        {searchProps?.enabled && positionAbove && (
                            <li
                                key={() => generateId()}
                                className="flex justify-between mx-2 mt-1 p-1 border border-secandary-600 rounded hover:border-secandary-600 focus:border-secandary-600"
                            >
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    autoComplete="off"
                                    name="searchInput"
                                    className="outline-none"
                                    value={searchString}
                                    onChange={(e) =>
                                        setSearchString(e.target.value)
                                    }
                                />

                                {Boolean(searchString) && (
                                    <Button
                                        icon={XIcon}
                                        iconSize={12}
                                        appearance="flat"
                                        variant="neutral"
                                        size="xs"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchString('');
                                        }}
                                    />
                                )}
                            </li>
                        )}
                    </ul>,
                    document.body
                )}
        </div>
    );
};

export default Select;
